import React, { useState, useRef, useEffect, useMemo, memo, useCallback } from 'react'
import { MdCheckBoxOutlineBlank } from 'react-icons/md'
import { IoIosCheckbox } from 'react-icons/io'
import { FaRegCircle, FaRegCircleDot } from 'react-icons/fa6'

const Dropdown = memo(
  ({
    options,
    selectedOptions,
    onSelect,
    dropdownLabel,
    multipleSelect,
    handleAllSelect,
    uniqueId,
    loading,
    dropContainerStyle = '',
    buttonStyle = '',
    dropdownMenuStyle = '',
    downArrStyle = '',
    showSelection = true,
    sortDropdown,
    sortOrder,
  }) => {
    const [isDropdownOpen, setDropdownOpen] = useState(false)
    const [allOptionSelected, setAllOptionSelected] = useState(false)
    const dropdownContainerRef = useRef(null)

    // Memoize functions to prevent unnecessary re-renders
    const toggleDropdown = useCallback((value) => {
      setDropdownOpen(value)
    }, [])

    const handleOptionClick = useCallback(
      (option) => {
        if (onSelect) {
          // Stop event propagation to prevent unexpected behaviors
          event?.stopPropagation()
          onSelect(option)
        }
      },
      [onSelect]
    )

    const handleAllSelectButtonClick = useCallback(
      (value) => {
        if (handleAllSelect) {
          // Stop event propagation to prevent unexpected behaviors
          event?.stopPropagation()
          setAllOptionSelected(!allOptionSelected)
          handleAllSelect(value)
        }
      },
      [handleAllSelect, allOptionSelected]
    )

    // Handler for clicking outside dropdown
    const handleOutsideClick = useCallback((event) => {
      if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }, [])

    // Update allOptionSelected state when dependencies change
    useEffect(() => {
      if (options && selectedOptions) {
        setAllOptionSelected(selectedOptions.length === options.length)
      }
    }, [selectedOptions, options])

    // Add and remove click event listener for outside clicks
    useEffect(() => {
      document.addEventListener('click', handleOutsideClick)
      return () => {
        document.removeEventListener('click', handleOutsideClick)
      }
    }, [handleOutsideClick])

    // Memoize the computed label for better performance
    const displayLabel = useMemo(() => {
      if (!multipleSelect && showSelection) {
        return (
          <span className="text-sm text-[#767676]">
            {dropdownLabel} <span className="text-sm font-bold text-black">{selectedOptions}</span>
          </span>
        )
      }
      return <span className={`text-sm text-[#767676] ${multipleSelect ? 'mr-4' : ''}`}>{dropdownLabel}</span>
    }, [dropdownLabel, selectedOptions, multipleSelect, showSelection])

    // Only compute options list when dropdown is open
    const optionsList = useMemo(() => {
      if (!isDropdownOpen) return null

      return (
        <div
          className={`absolute overflow-y-auto md:bottom-auto bottom-14 top-12 !left-auto z-10 py-[20px] px-[10px] rounded-xl flex flex-col gap-2 flex-shrink-0 min-w-[200px] max-h-[400px] bg-white shadow-[0_6.333px_31.663px_0px_rgb(0,0,0,0.25)] cursor-pointer ${dropdownMenuStyle}`}
        >
          {multipleSelect && (
            <>
              <div
                className="flex items-center cursor-pointer gap-2 rounded-2xl bg-white shadow-[0_6px_31px_0px_rgba(0,,0,0,2.5)]"
                onClick={() => handleAllSelectButtonClick(!allOptionSelected)}
              >
                {allOptionSelected ? (
                  <IoIosCheckbox color="#000" size={26} />
                ) : (
                  <MdCheckBoxOutlineBlank opacity={0.1} size={26} />
                )}
                <span className="text-xs">All</span>
              </div>
              <div className="w-full h-[2px] bg-[#D9D9D9]"></div>
            </>
          )}
          {options?.map((option) => (
            <div
              key={`dropdown-option-${option.label}`}
              className="flex items-center cursor-pointer gap-2 rounded-2xl bg-white shadow-[0_6px_31px_0px_rgba(0,,0,0,2.5)]"
              onClick={() => handleOptionClick(option.value)}
            >
              {!multipleSelect ? (
                selectedOptions === option.value ? (
                  <FaRegCircleDot color="#000" size={18} />
                ) : (
                  <FaRegCircle opacity={0.1} size={18} />
                )
              ) : null}
              {multipleSelect ? (
                selectedOptions.includes(option.value) ? (
                  <IoIosCheckbox color="#000" size={26} />
                ) : (
                  <MdCheckBoxOutlineBlank opacity={0.1} size={26} />
                )
              ) : null}
              <span className="text-xs">{option?.label}</span>
            </div>
          ))}
        </div>
      )
    }, [
      isDropdownOpen,
      dropdownMenuStyle,
      multipleSelect,
      allOptionSelected,
      options,
      selectedOptions,
      handleOptionClick,
      handleAllSelectButtonClick,
    ])

    return (
      <div className={`relative !text-black w-full ${!multipleSelect && 'min-w-[165px]'} ${dropContainerStyle}`}>
        <button
          id={`dropdownBgHoverButton_${uniqueId}`}
          className={`!text-[#000] border w-full border-[#D1D5DB] border-solid text-center text-[16px] font-normal -tracking-[0.063px] appearance-none rounded-lg text-sm px-3 py-2.5 inline-flex items-center justify-between ${loading ? 'opacity-30 pointer-events-none cursor-none' : ''} ${buttonStyle}`}
          type="button"
          onClick={() => toggleDropdown(!isDropdownOpen)}
          ref={dropdownContainerRef}
          disabled={loading}
        >
          {displayLabel}
          <div className="flex items-center">
            {sortDropdown && <span className="mr-2 text-xs font-semibold">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
            <img
              src="/assets/common/down-arrow-news.svg"
              alt="arrow-down"
              width={16}
              height={16}
              className={`text-black cursor-pointer news-arrow-down w-[10px] h-[10px] md:w-[16px] md:h-[16px] ${downArrStyle}`}
              loading="eager"
            />
          </div>
        </button>

        {optionsList}
      </div>
    )
  }
)

Dropdown.displayName = 'Dropdown'

export default Dropdown
